import { onMounted, ref, watch, type Ref } from 'vue';
import { AppContext } from '@/models/AppContext';
import { isCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { useAuthStore } from '@/shared/stores/authStore';
import { isLoggedIn as checkIsLoggedIn } from '@/shared/auth/session';

/** Reactive login state for templates; refreshes from session / legacy store. */
export function useIsLoggedIn(appContext: AppContext): Ref<boolean> {
  const authStore = useAuthStore();
  const loggedIn = ref(
    isCognitoEnabled ? false : authStore.isLegacyLoggedIn(appContext),
  );

  async function refresh() {
    loggedIn.value = await checkIsLoggedIn(appContext);
  }

  onMounted(() => void refresh());

  if (isCognitoEnabled) {
    watch(
      () => [authStore.cognitoProfile, authStore.token, authStore.exp],
      () => void refresh(),
      { deep: true },
    );
  } else if (appContext === AppContext.ComApp) {
    watch(() => [authStore.token, authStore.exp], () => {
      loggedIn.value = authStore.isLegacyLoggedIn(appContext);
    });
  } else {
    watch(
      () => authStore.legacySolcentreLoginUser,
      () => {
        loggedIn.value = authStore.isLegacyLoggedIn(appContext);
      },
      { deep: true },
    );
  }

  return loggedIn;
}
